import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { loadHrisIntegrationResponse } from '../../hris-integration-response/actions/load-hris-integration-response';
import { hrisIntegrationResponseForOrgIdSelector } from '../../hris-integration-response/hris-integration-response-selectors';
import { loadMergeIntegration } from '../../merge-integration/actions/load-merge-integration';
import { mergeIntegrationForOrgIdSelector } from '../../merge-integration/merge-integration-selectors';

import { PayrollIntegrationButton } from '../../merge-integration/payroll-integration-button';
import { UiButton } from '../../ui/ui-button';

import { SyncWithInovaDialog } from './sync-with-inova-dialog';

import { inovaReadDescription } from './utils/inova-component-utils';
import { MERGE_SYNC_STATUS } from '../../merge-integration/util/merge-sync-status';
import { EmailSupportLink } from '../../widgets/email-support-link';

export const SyncWithInova = ({ org }: { org: Org }) => {
  const orgId = org.org_id;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadMergeIntegration(orgId));
    dispatch(loadHrisIntegrationResponse(orgId));
  });

  const mergeIntegration = useSelector((state: any) =>
    mergeIntegrationForOrgIdSelector(state, orgId),
  );
  const hrisResponse = useSelector((state: any) =>
    hrisIntegrationResponseForOrgIdSelector(state, orgId),
  );

  // If the org has an active HRIS integration we will show them the manage button and allow them to pause to switch to Inova
  // If the org has a pending HRIS integration we will show a disabled button and allow the current sync set up process to finish
  // If the org has no pending HRIS integration we will show the button to sync with Inova
  const hasHrisIntegration = !!mergeIntegration;
  const hasPendingHrisIntegration = !!hrisResponse && !mergeIntegration;
  const hasPausedHrisIntegration = mergeIntegration?.syncStatus === MERGE_SYNC_STATUS.IMPORT_PAUSED;
  const integrationIsInova = hrisResponse?.HrisIntegrationType?.integrationName === 'Inova';
  const disableRequest =
    hasPendingHrisIntegration || (hasHrisIntegration && !hasPausedHrisIntegration);

  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const handleToggleSync = () => {
    setIsSyncing(!isSyncing);
  };
  return (
    <>
      <div className="typo-subtitle u-maxWidth-750px u-margin-y-lg">{inovaReadDescription}</div>
      {hasHrisIntegration && !integrationIsInova && (
        <div className="typo-subtitle u-maxWidth-750px u-margin-bottom-lg u-margin-right">
          Your company currently has an active sync with another payroll. To switch to syncing with
          Inova, please pause your current payroll integration and then request the Inova sync.
        </div>
      )}
      {hasPendingHrisIntegration && !integrationIsInova && (
        <div className="typo-subtitle u-maxWidth-750px u-margin-bottom-lg u-margin-right">
          Your company is currently setting up a sync with another payroll. To switch to syncing
          with Inova, please contact <EmailSupportLink /> and provide your Inova
          representative&apos;s contact information to request the Inova sync.
        </div>
      )}
      <div className="u-display-flex u-margin-bottom">
        {hasHrisIntegration && <PayrollIntegrationButton org={org} />}
        {(hasPausedHrisIntegration || !hasHrisIntegration) && (
          <UiButton
            className={`${disableRequest ? '' : 'u-background-inova u-color-black'} ${
              hasHrisIntegration ? 'u-margin-left' : ''
            }`}
            size="sm"
            onClick={handleToggleSync}
            disabled={disableRequest}
          >
            {hasPendingHrisIntegration ? 'Setup in progress' : 'Request Sync with Inova'}
          </UiButton>
        )}
      </div>
      {isSyncing && <SyncWithInovaDialog orgId={orgId} onClose={handleToggleSync} />}
    </>
  );
};
